import {isFeatureEnabled} from '@github-ui/feature-flags'
import {IssueAgentTasksProvider} from '@github-ui/issue-agent-tasks/contexts/IssueAgentTasksContext'
import {UserNameDisplayContextProvider} from '@github-ui/profile-reference/UserNameDisplayContext'
import {TanStackCachePreheatContextProvider} from '@github-ui/relay-query-cache/contexts/RelayCachePreheatContext'
import {ArtifactsPanelProvider} from '@github-ui/scribe-artifacts/ArtifactPanelContext'
import {clsx} from 'clsx'
import type {ReactNode} from 'react'

import {IssueViewerContextProvider} from '../contexts/IssueViewerContext'
import {RelayLiveUpdatesContext} from '../contexts/relay-live-updates-context'
import type {IssueViewerIssue$data} from './__generated__/IssueViewerIssue.graphql'
import {ArtifactsPanelLayout} from './artifacts-section/ArtifactsPanelLayout'
import {IssueViewerInternalFragment, type IssueViewerViewerFragment} from './IssueViewer'
import styles from './IssueViewer.module.css'
import {useSecondaryQuery} from './IssueViewerSecondaryView'
import type {OptionConfig} from './OptionConfig'
import {SecondaryViewLiveUpdates} from './SecondaryViewLiveUpdates'
import {useIssueViewerContainerQueryFix} from './use-issue-viewer-container-query-fix'
import {useIssueViewerNavigationBlocker} from './use-issue-viewer-navigation-blocker'

export type DataRouterIssueViewerProps = {
  issue: IssueViewerIssue$data
  /** The oldest timeline section selected alongside the issue. */
  initialBackAnchor?: string
  viewer: IssueViewerViewerFragment | null
  isRepoOwnerEnterpriseManaged?: boolean | null
  userNameDisplayConfiguration?: string | null
  /** The route's own page-query key, so live updates refresh the header and sidebar it renders. */
  routeQueryKey?: readonly unknown[]
  /** `?timeline_page=N`, for the crawler's isolated historical window. */
  timelinePage?: number
  optionConfig: OptionConfig
}

/**
 * The issue viewer as the Data Router route renders it: the same component tree the Relay route
 * mounts, reading the page query's payload directly instead of through a Relay fragment.
 *
 * No data here comes from Relay, but leaves below still commit mutations through it. The route
 * provides that environment rather than this component, so the store survives issue navigation. The
 * preheat context schedules TanStack tasks only.
 */
export function DataRouterIssueViewer({
  issue,
  initialBackAnchor,
  viewer,
  isRepoOwnerEnterpriseManaged,
  userNameDisplayConfiguration,
  routeQueryKey,
  timelinePage,
  optionConfig,
}: DataRouterIssueViewerProps) {
  const owner = issue.repository.owner.login
  const repo = issue.repository.name
  const issueViewerContainerRef = useIssueViewerContainerQueryFix(owner, repo, issue.number)

  return (
    <div
      ref={issueViewerContainerRef}
      className={clsx(
        styles.issueViewerContainer,
        styles.mainContainer,
        isFeatureEnabled('issues_sticky_header') && styles.mainContainer_stickyHeader,
      )}
    >
      <TanStackCachePreheatContextProvider>
        <DataRouterIssueViewerContent
          issue={issue}
          initialBackAnchor={initialBackAnchor}
          viewer={viewer}
          isRepoOwnerEnterpriseManaged={isRepoOwnerEnterpriseManaged}
          userNameDisplayConfiguration={userNameDisplayConfiguration}
          routeQueryKey={routeQueryKey}
          timelinePage={timelinePage}
          optionConfig={optionConfig}
        />
      </TanStackCachePreheatContextProvider>
    </div>
  )
}

/** Split from the component above so `useSecondaryQuery` runs under the preheat context. */
function DataRouterIssueViewerContent({
  issue,
  initialBackAnchor,
  viewer,
  isRepoOwnerEnterpriseManaged,
  userNameDisplayConfiguration,
  routeQueryKey,
  timelinePage,
  optionConfig,
}: DataRouterIssueViewerProps) {
  const owner = issue.repository.owner.login
  const repo = issue.repository.name

  const [secondaryIssueData, hideClosedSubIssuesEnabled, initialNestedSubIssuesData] = useSecondaryQuery({
    owner,
    repo,
    number: issue.number,
  })

  return (
    <RelayLiveUpdatesContext value={false}>
      <SecondaryViewLiveUpdates
        channel={secondaryIssueData?.updatesChannel}
        owner={owner}
        repo={repo}
        number={issue.number}
        issueId={issue.id}
        routeQueryKey={routeQueryKey}
      />
      <IssueAgentTasksProvider owner={owner} repo={repo} issueNumber={issue.number}>
        <UserNameDisplayContextProvider value={userNameDisplayConfiguration ?? undefined}>
          <ArtifactsPanelProvider>
            <DataRouterArtifactsLayout insideSidePanel={optionConfig.insideSidePanel}>
              <IssueViewerContextProvider>
                <DataRouterIssueViewerInternal
                  issueFragment={null}
                  issueData={issue}
                  initialBackAnchor={initialBackAnchor}
                  viewer={viewer}
                  isRepoOwnerEnterpriseManaged={isRepoOwnerEnterpriseManaged}
                  secondaryIssueData={secondaryIssueData ?? undefined}
                  initialNestedSubIssuesData={initialNestedSubIssuesData}
                  hideClosedSubIssuesEnabled={hideClosedSubIssuesEnabled}
                  timelinePage={timelinePage}
                  issueUpdatesChannel={secondaryIssueData?.updatesChannel}
                  routeQueryKey={routeQueryKey}
                  optionConfig={optionConfig}
                />
              </IssueViewerContextProvider>
            </DataRouterArtifactsLayout>
          </ArtifactsPanelProvider>
        </UserNameDisplayContextProvider>
      </IssueAgentTasksProvider>
    </RelayLiveUpdatesContext>
  )
}

export function DataRouterArtifactsLayout({
  children,
  insideSidePanel,
}: {
  children: ReactNode
  insideSidePanel?: boolean
}) {
  return <ArtifactsPanelLayout insideSidePanel={insideSidePanel}>{children}</ArtifactsPanelLayout>
}

function DataRouterIssueViewerInternal(props: Parameters<typeof IssueViewerInternalFragment>[0]) {
  useIssueViewerNavigationBlocker()

  return <IssueViewerInternalFragment {...props} />
}
